<script setup lang="ts">
// {{{ imports
import { AliyahType, BarMitzvahType, HaftarahType, SentenceGroupType, SentenceType, TagType } from 'types/types'
import { computed, onBeforeUnmount, onMounted, reactive, Ref, ref, toRefs, watch } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

import LegacyPracticeAllText from '@/components/LegacyPracticeAllText.vue'
import LegacyPracticeOverlay from '@/components/LegacyPracticeOverlay.vue'
import LegacyPracticeSelect from '@/components/LegacyPracticeSelect.vue'
import LegacyPracticeTagSelection from '@/components/LegacyPracticeTagSelection.vue'
import LegacyPracticeTextToolbar from '@/components/LegacyPracticeTextToolbar.vue'
import RecordingUploadDialog from '@/components/RecordingUploadDialog.vue'
import SamplingOverlay from '@/components/SamplingOverlay.vue'
import StudentSelect from '@/components/StudentSelect.vue'
import VoiceSelect from '@/components/VoiceSelect.vue'
import { useAudioPlayer } from '@/composables/audioPlayer'
import { useChoosableTags } from '@/composables/choosableTags'
import { useFollower } from '@/composables/follower'
import { useFont } from '@/composables/font'
import { formatPracticeDescription } from '@/composables/format'
import { useResponsiveness } from '@/composables/responsive'
import { useSegmentation } from '@/composables/segmentation'
import { useSentences } from '@/composables/sentences'
import { useStyledRecordings } from '@/composables/styledRecording'
import { PlayPhase, ReadOrder, studentParticipates, usePracticeFSM } from '@/composables/usePracticeFSM'
import {
  type PracticeNavigationDeps,
  UI_MAX_PITCH_SHIFT,
  UI_MIN_PITCH_SHIFT,
  usePracticeNavigation,
} from '@/composables/usePracticeNavigation'
import { usePracticeSession } from '@/composables/usePracticeSession'
import { usePracticeStyles } from '@/composables/usePracticeStyles'
import { usePracticeTextStyles } from '@/composables/usePracticeTextStyles'
import { type FocusPoint, type SegmentPoint, usePracticeUpload, type VoiceEvent } from '@/composables/usePracticeUpload'
import { usePracticeURL } from '@/composables/usePracticeURL'
import { useUtil } from '@/composables/util'
import { DEFAULT_SILENCE_PATIENCE, NEXT_KEY, practiceTextSegmentId, VOICE_SENSITIVITY_DEFAULT } from '@/constants/practiceConstants'
import store from '@/store'
import { AnnotatedSegment } from '@/util/AnnotatedSegment'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'
import { DEFAULT_PITCH_SHIFT, HTMLAudioWrapper, PitchShiftType, SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import { PracticeSettings } from '@/util/PracticeSettings'
import { RecorderService } from '@/util/RecorderService'
import type { RecordingType } from '@/util/Recording'
import { TransformationType } from '@/util/Transformations'
import { inTextMode, READ, VoiceStyles, VoiceStyleSet } from '@/util/VoiceStyles'
// }}}
// {{{ enum, interface
interface PracticeComponentData {
  aliyah: AliyahType | null
  annotatedSentences: Array<AnnotatedSentence>
  autoAdvance: boolean
  automaticallyFlippedVoiceStyle: boolean
  backgroundSampling: boolean
  backgroundSamplingPercent: number
  choosableHiddenTagsListeners: Set<() => void>
  choosableNonHiddenTagsListeners: Set<() => void>
  downloading: boolean
  finalizerHandler: NodeJS.Timeout | null
  focusAnnotatedSentence: AnnotatedSentence | null
  focusSentenceIndex: number | null
  pendingRecording: RecordingType | null
  pendingRecordingBlobUrl: string | null
  focusSentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios | null
  focusPoints: Array<FocusPoint>
  fontSize: number
  fsmHistory: Array<string>
  fsmState: number
  haftarah: HaftarahType | null
  inRepeatMode: boolean
  jumpBackPressed: boolean
  manualForwardKeyPressed: boolean
  manualSegmentJump: boolean
  pitchShift: PitchShiftType
  playCounter: number
  playPhase: PlayPhase
  playingSentenceOrSegmentAudio: HTMLAudioWrapper | null
  popoverSegment: AnnotatedSegment | null
  popoverSegmentIndex: number | null
  popoverSentence: AnnotatedSentence | null
  popoverSentenceIndex: number | null
  popoverShowStartNow: boolean
  popoverVisible: boolean
  readOrder: ReadOrder
  recordButtonPressed: boolean
  recorderService: RecorderService | null
  recordMe: boolean
  requiredRepeats: number
  segmentClicked: number | null
  segmentPoints: Array<SegmentPoint>
  segmentation: Array<number> | null
  selectedHiddenTag: TagType | null
  selectedNonHiddenTags: Array<TagType>
  selectedBarMitzvah: BarMitzvahType | null
  sentenceGroup: SentenceGroupType | null
  sentences: Array<SentenceType>
  silencePatience: number
  silenceStartedAt: Date | null
  recordingStartedAt: number
  soundLevel: number
  surroundLines: number
  tikkunStyle: boolean
  transformation: TransformationType | null
  uiPitchShift: number
  uploading: boolean
  uploadRecordingOverlay: boolean
  voiceEvents: Array<VoiceEvent>
  voiceSensitivity: number
  voiceStyleSet: VoiceStyleSet
  waitForVoiceEnd: boolean
  waitForVoiceStart: boolean
}

// }}}
// {{{ const
const DEBUG = false
const logger = useLogger()
const { isProduction, isTeacher } = useUtil(logger)
const { MAX_FONT_SIZE, MIN_FONT_SIZE, fontSize, lineHeight } = useFont()

const state = reactive<PracticeComponentData>({
  aliyah: null,
  annotatedSentences: [] as Array<AnnotatedSentence>,
  autoAdvance: false,
  automaticallyFlippedVoiceStyle: false,
  backgroundSampling: false,
  backgroundSamplingPercent: 0,
  choosableHiddenTagsListeners: new Set(),
  choosableNonHiddenTagsListeners: new Set(),
  downloading: false,
  finalizerHandler: null,
  // focusAnnotatedSentence is NOT a computed of focusSentenceIndex — they can
  // be out of sync during transitions (e.g., sentence highlighted but not focused).
  focusAnnotatedSentence: null,
  focusPoints: [],
  focusSentenceIndex: null,
  focusSentenceRecordingMetadataWithPitchedAudios: null,
  fontSize: fontSize.value,
  fsmHistory: [],
  fsmState: 0,
  haftarah: null,
  inRepeatMode: false,
  jumpBackPressed: false,
  manualForwardKeyPressed: false,
  manualSegmentJump: false,
  pendingRecording: null,
  pendingRecordingBlobUrl: null,
  pitchShift: DEFAULT_PITCH_SHIFT,
  playCounter: 0,
  playPhase: PlayPhase.FIRST,
  playingSentenceOrSegmentAudio: null,
  popoverSegment: null,
  popoverSegmentIndex: null,
  popoverSentence: null,
  popoverSentenceIndex: null,
  popoverShowStartNow: false,
  popoverVisible: false,
  readOrder: ReadOrder.STUDENT_TEACHER,
  recordButtonPressed: false,
  recordMe: false,
  recorderService: null,
  recordingStartedAt: 0,
  requiredRepeats: 1,
  segmentClicked: null,
  segmentPoints: [],
  segmentation: null,
  selectedBarMitzvah: null,
  selectedHiddenTag: null,
  selectedNonHiddenTags: [],
  sentenceGroup: null,
  sentences: [],
  silencePatience: DEFAULT_SILENCE_PATIENCE,
  silenceStartedAt: null,
  soundLevel: 0,
  surroundLines: 0,
  tikkunStyle: false,
  transformation: null,
  uiPitchShift: 0,
  uploadRecordingOverlay: false,
  uploading: false,
  voiceEvents: [],
  voiceSensitivity: VOICE_SENSITIVITY_DEFAULT,
  voiceStyleSet: {
    haftarahMelody: null,
    pronunciation: null,
    sentenceGroupMelody: null,
    tags: [],
    torahMelody: null,
    voice: VoiceStyles[READ],
  },
  waitForVoiceEnd: false,
  waitForVoiceStart: false,
})

const refsState = toRefs(state)
const pendingStudentBarMitzvah = ref<BarMitzvahType | null>(null)
const { baseLevel, currentSegmentIndex, currentSentenceIndex, currentStyle, followerOptions, followerSegmentStyle, setCurrentSegment } =
  useFollower(refsState.recorderService as Ref<RecorderService>)
const { isPlayable, loaded, pause, paused, play, playing, purge, setLoadPlay, unloaded } = useAudioPlayer(logger)

const { recordingsInStyle } = useStyledRecordings(refsState.annotatedSentences as Ref<Array<AnnotatedSentence>>, refsState.voiceStyleSet)
const { sentencesHaveSameResponsiveness } = useResponsiveness(refsState.sentences as Ref<Array<SentenceType>>)
const { watchSentences } = useSentences(refsState.sentences, logger)
watchSentences()

const { choosableHiddenTags, choosableNonHiddenTags, choosableTags } = useChoosableTags(
  refsState.sentences,
  refsState.voiceStyleSet,
  logger,
)

const router = useRouter()
const route = useRoute()

// {{{ computed sentence
const sentence = computed<SentenceType | null>((): SentenceType | null => {
  return state.focusAnnotatedSentence ? state.focusAnnotatedSentence.sentence() : null
})
// }}}
// {{{ computed isSegmented
/**
 * @invariant True iff focused recording has an associated segmentedSentence
 * @depends-on state.focusSentenceRecordingMetadataWithPitchedAudios
 */
const isSegmented = computed<boolean>(() => {
  if (!state.focusSentenceRecordingMetadataWithPitchedAudios?.sentenceRecording) {
    logger.debug('unexpected null state.focusSentenceRecordingMetadataWithPitchedAudios.sentenceRecording in isSegmented')
    return false
  }

  return (
    !!state.focusSentenceRecordingMetadataWithPitchedAudios &&
    !!state.focusSentenceRecordingMetadataWithPitchedAudios.sentenceRecording.segmentedSentence
  )
})
// }}}
// {{{ computed isRecording (needs both FSM and Session state)
/**
 * @invariant True when either: (a) RecorderService is actively recording, OR (b) in TEACHER_ONLY
 *   mode and the record button was pressed (TEACHER_ONLY doesn't use RecorderService)
 * @depends-on state.readOrder, state.recordButtonPressed, state.recorderService
 */
const isRecording = computed<boolean>((): boolean => {
  return (!studentParticipates(state.readOrder) && state.recordButtonPressed) || (state.recorderService?.isRecording() ?? false)
})
// }}}

// {{{ composable construction (Upload — constructed before Nav, FSM, Session since they need pushFocusPoint)
const practiceUpload = usePracticeUpload(logger, {
  aliyah: refsState.aliyah,
  focusPoints: refsState.focusPoints,
  focusSentenceIndex: refsState.focusSentenceIndex,
  focusSentenceRecordingMetadataWithPitchedAudios: refsState.focusSentenceRecordingMetadataWithPitchedAudios,
  haftarah: refsState.haftarah,
  readOrder: refsState.readOrder,
  recordingStartedAt: refsState.recordingStartedAt,
  segmentPoints: refsState.segmentPoints,
  sentence,
  sentenceGroup: refsState.sentenceGroup,
  store,
  uploadRecordingOverlay: refsState.uploadRecordingOverlay,
  uploading: refsState.uploading,
  voiceEvents: refsState.voiceEvents,
  voiceStyleSet: refsState.voiceStyleSet,
})
// }}}

// {{{ composable construction (Navigation — constructed first, late-binds toggleRecord + driveState)
const nav = usePracticeNavigation(logger, {
  annotatedSentences: refsState.annotatedSentences as Ref<Array<AnnotatedSentence>>,
  downloading: refsState.downloading,
  focusAnnotatedSentence: refsState.focusAnnotatedSentence as Ref<AnnotatedSentence | null>,
  focusSentenceIndex: refsState.focusSentenceIndex,
  focusSentenceRecordingMetadataWithPitchedAudios: refsState.focusSentenceRecordingMetadataWithPitchedAudios,
  fsmHistory: refsState.fsmHistory,
  fsmState: refsState.fsmState,
  isRecording,
  isSegmented,
  pause,
  pitchShift: refsState.pitchShift,
  playing,
  popoverSegment: refsState.popoverSegment as Ref<AnnotatedSegment | null>,
  popoverSegmentIndex: refsState.popoverSegmentIndex,
  popoverSentence: refsState.popoverSentence as Ref<AnnotatedSentence | null>,
  popoverSentenceIndex: refsState.popoverSentenceIndex,
  popoverVisible: refsState.popoverVisible,
  purge,
  pushFocusPoint: practiceUpload.pushFocusPoint,
  recordingsInStyle,
  segmentClicked: refsState.segmentClicked,
  segmentation: refsState.segmentation,
  sentence,
  sentences: refsState.sentences,
  setCurrentSegment,
  setLoadPlay: setLoadPlay as PracticeNavigationDeps['setLoadPlay'],
  store,
  surroundLines: refsState.surroundLines,
  uiPitchShift: refsState.uiPitchShift,
  voiceStyleSet: refsState.voiceStyleSet,
})

// Destructure nav returns for template and watcher use
const {
  advanceToSentence,
  closePopover,
  closePracticeOverlay,
  handleSegmentBoundaryClick,
  handleSegmentClick,
  handleSentenceClick,
  hasRecordingByAnnotatedSentence,
  hasRecordingBySentenceIndex,
  refetchAudio,
  surroundAfterSentences,
  surroundBeforeSentences,
  surroundBeforeStartIndex,
} = nav

// useSegmentation depends on surroundBeforeSentences from nav, so it's constructed after nav
const { resetSegments, segmentations } = useSegmentation(
  refsState.segmentation as Ref<Array<number>>,
  surroundBeforeSentences as Ref<Array<SentenceType>>,
  logger,
)
nav.setResetSegments(resetSegments)
// }}}

// {{{ composable construction (TextStyles — constructed after Navigation for hasRecording callbacks)
const textStyles = usePracticeTextStyles(logger, {
  currentSegmentIndex,
  currentSentenceIndex,
  downloading: refsState.downloading,
  focusSentenceIndex: refsState.focusSentenceIndex,
  followerSegmentStyle,
  hasRecordingByAnnotatedSentence,
  hasRecordingBySentenceIndex,
  isPlayable,
  isRecording,
  pitchShift: refsState.pitchShift,
  recordingsInStyle,
  surroundBeforeSentences,
  surroundBeforeStartIndex,
  voiceStyleSet: refsState.voiceStyleSet,
})
const { clickableSegment, clickableSentence, segmentBoundaryStyle, segmentStyle, sentenceStyle } = textStyles
// }}}

// {{{ composable construction (Styles — constructed after Navigation, late-binds toggleRecord)
const styles = usePracticeStyles(logger, {
  aliyah: refsState.aliyah,
  automaticallyFlippedVoiceStyle: refsState.automaticallyFlippedVoiceStyle,
  choosableHiddenTags,
  choosableHiddenTagsListeners: refsState.choosableHiddenTagsListeners,
  choosableNonHiddenTags,
  choosableNonHiddenTagsListeners: refsState.choosableNonHiddenTagsListeners,
  choosableTags,
  closePracticeOverlay,
  focusAnnotatedSentence: refsState.focusAnnotatedSentence as Ref<AnnotatedSentence | null>,
  focusSentenceIndex: refsState.focusSentenceIndex,
  haftarah: refsState.haftarah,
  handleSentenceClick,
  hasRecordingByAnnotatedSentence,
  isRecording,
  manualSegmentJump: refsState.manualSegmentJump,
  recordingsInStyle,
  selectedBarMitzvah: refsState.selectedBarMitzvah,
  selectedHiddenTag: refsState.selectedHiddenTag,
  selectedNonHiddenTags: refsState.selectedNonHiddenTags,
  sentence,
  sentenceGroup: refsState.sentenceGroup,
  sentences: refsState.sentences,
  store,
  tikkunStyle: refsState.tikkunStyle,
  transformation: refsState.transformation,
  voiceStyleSet: refsState.voiceStyleSet,
})

// Destructure styles returns for template use
const { hiddenTagsStats, isInSingMode, isInTextMode, relevantTransformations, tagWarning, tagWarningBox } = styles
// }}}

// {{{ composable construction (URL — constructed after Styles for relevantTransformations)
const practiceURL = usePracticeURL(logger, {
  aliyah: refsState.aliyah,
  annotatedSentences: refsState.annotatedSentences as Ref<Array<AnnotatedSentence>>,
  choosableHiddenTags,
  choosableHiddenTagsListeners: refsState.choosableHiddenTagsListeners,
  choosableNonHiddenTags,
  focusSentenceIndex: refsState.focusSentenceIndex,
  haftarah: refsState.haftarah,
  handleSentenceClick,
  readOrder: refsState.readOrder,
  relevantTransformations,
  route,
  selectedHiddenTag: refsState.selectedHiddenTag,
  selectedNonHiddenTags: refsState.selectedNonHiddenTags,
  sentenceGroup: refsState.sentenceGroup,
  store,
  surroundLines: refsState.surroundLines,
  tikkunStyle: refsState.tikkunStyle,
  transformation: refsState.transformation,
  voiceStyleSet: refsState.voiceStyleSet,
})
const { copyUrlToClipboard, restoreStateFromPracticeSettings } = practiceURL
// }}}

// {{{ composable construction (FSM + Session)
const fsm = usePracticeFSM(logger, {
  advanceToSentence,
  autoAdvance: refsState.autoAdvance,
  currentSegmentIndex,
  currentSentenceIndex,
  currentStyle,
  finalizerHandler: refsState.finalizerHandler,
  focusSentenceIndex: refsState.focusSentenceIndex,
  focusSentenceRecordingMetadataWithPitchedAudios: refsState.focusSentenceRecordingMetadataWithPitchedAudios,
  fsmHistory: refsState.fsmHistory,
  fsmState: refsState.fsmState,
  hasRecordingBySentenceIndex,
  inRepeatMode: refsState.inRepeatMode,
  isRecording,
  isSegmented,
  jumpBackPressed: refsState.jumpBackPressed,
  manualForwardKeyPressed: refsState.manualForwardKeyPressed,
  manualSegmentJump: refsState.manualSegmentJump,
  pause,
  pitchShift: refsState.pitchShift,
  playCounter: refsState.playCounter,
  playPhase: refsState.playPhase,
  playing,
  popoverSegment: refsState.popoverSegment,
  popoverSegmentIndex: refsState.popoverSegmentIndex,
  popoverSentence: refsState.popoverSentence,
  popoverSentenceIndex: refsState.popoverSentenceIndex,
  popoverShowStartNow: refsState.popoverShowStartNow,
  popoverVisible: refsState.popoverVisible,
  pushFocusPoint: practiceUpload.pushFocusPoint,
  pushSegmentPoint: practiceUpload.pushSegmentPoint,
  readOrder: refsState.readOrder,
  recorderService: refsState.recorderService as Ref<RecorderService | null>,
  recordingsInStyle,
  requiredRepeats: refsState.requiredRepeats,
  segmentClicked: refsState.segmentClicked,
  sentence,
  setCurrentSegment,
  setLoadPlay,
  waitForVoiceEnd: refsState.waitForVoiceEnd,
  waitForVoiceStart: refsState.waitForVoiceStart,
})

const session = usePracticeSession(logger, {
  advanceToSentence,
  aliyah: refsState.aliyah,
  backgroundSampling: refsState.backgroundSampling,
  backgroundSamplingPercent: refsState.backgroundSamplingPercent,
  baseLevel,
  closePopover,
  closePracticeOverlay,
  currentSegmentIndex,
  currentSentenceIndex,
  driveState: fsm.driveState,
  finalizerHandler: refsState.finalizerHandler,
  focusAnnotatedSentence: refsState.focusAnnotatedSentence,
  focusPoints: refsState.focusPoints,
  focusSentenceIndex: refsState.focusSentenceIndex,
  followerOptions,
  fsmHistory: refsState.fsmHistory,
  fsmState: refsState.fsmState,
  hasRecordingBySentenceIndex,
  initializeRecordingState: fsm.initializeRecordingState,
  isProduction,
  isRecording,
  isSegmented,
  manualForwardKeyPressed: refsState.manualForwardKeyPressed,
  manualSegmentJump: refsState.manualSegmentJump,
  pause,
  pendingRecording: refsState.pendingRecording,
  pendingRecordingBlobUrl: refsState.pendingRecordingBlobUrl,
  playing,
  popoverShowStartNow: refsState.popoverShowStartNow,
  popoverVisible: refsState.popoverVisible,
  practiceUpload,
  readOrder: refsState.readOrder,
  recordButtonPressed: refsState.recordButtonPressed,
  recordMe: refsState.recordMe,
  recorderService: refsState.recorderService as Ref<RecorderService | null>,
  recordingStartedAt: refsState.recordingStartedAt,
  segmentPoints: refsState.segmentPoints,
  sentence,
  setCurrentSegment,
  silencePatience: refsState.silencePatience,
  silenceStartedAt: refsState.silenceStartedAt,
  soundLevel: refsState.soundLevel,
  transformation: refsState.transformation,
  uploadRecordingOverlay: refsState.uploadRecordingOverlay,
  uploading: refsState.uploading,
  voiceEvents: refsState.voiceEvents,
  voiceSensitivity: refsState.voiceSensitivity,
  waitForVoiceEnd: refsState.waitForVoiceEnd,
  waitForVoiceStart: refsState.waitForVoiceStart,
})

// Wire circular dependencies
fsm.setToggleRecord(session.toggleRecord) // FSM needs toggleRecord from Session
nav.setToggleRecord(session.toggleRecord) // Navigation needs toggleRecord from Session (closePracticeOverlay)
nav.setDriveState(fsm.driveState) // Navigation needs driveState from FSM (handleSegmentClick)
styles.setToggleRecord(session.toggleRecord) // Styles needs toggleRecord from Session (voiceStyleSet watcher)
// }}}

// }}}

// {{{ cleanup
let cleanupKeyboardHandler: (() => void) | null = null

onBeforeRouteLeave((__, ___, next) => {
  if (isRecording.value) {
    session.toggleRecord()
  }
  next()
})

onBeforeUnmount(() => {
  if (cleanupKeyboardHandler) {
    cleanupKeyboardHandler()
    cleanupKeyboardHandler = null
  }
})
// }}}

onMounted(() => {
  // {{{ init
  store.dispatch.audio.setLogger(logger)
  store.dispatch.user.fetchUser()

  if (isTeacher()) {
    store.dispatch.teacher.fetchLightBarMitzvot()
    watch(pendingStudentBarMitzvah, async (bm) => {
      state.selectedBarMitzvah = bm ? await store.dispatch.teacher.fetchFullBarMitzvah(bm) : null
    })
  } else {
    // restore practice settings if relevant
    store.dispatch.student.fetchBarMitzvah().then(() => {
      if (store.getters.student.practiceSettings) {
        restoreStateFromPracticeSettings()
        store.dispatch.student.clearPracticeSettings()
      }
    })
  }

  // if we're coming with a long URL, store the settings and route back to /practice
  if (route.query.a || route.query.h || route.query.sg) {
    const ps: PracticeSettings = {
      a: route.query.a?.toString() || null,
      fs: route.query.fs?.toString() || null,
      h: route.query.h?.toString() || null,
      ht: route.query.ht?.toString() || null,
      nht: route.query.nht?.toString() || null,
      ro: route.query.ro?.toString() || null,
      sg: route.query.sg?.toString() || null,
      sl: route.query.sl?.toString() || null,
      t: route.query.t?.toString() || null,
      tk: route.query.tk?.toString() || null,
      v: route.query.v?.toString() || null,
    }
    store.dispatch.student.setPracticeSettings(ps)
    logger.debug(`PracticeView, onMounted, router.push ${route.path}`)
    router.push(route.path)
  }
  // }}}
  // {{{ keyboard handler + RecorderService init (delegated to composables)
  cleanupKeyboardHandler = session.setupKeyboardHandler()
  session.initRecorderService()
  // }}}

  // {{{ style/tag watchers (delegated to styles composable — 15 watchers in exact original order)
  styles.registerWatchers()
  // }}}

  // {{{ watch state.surroundLines
  /**
   * @watches state.surroundLines
   * @invariant currentSentenceIndex stays correct relative to surround offset after surroundLines changes
   * @postcondition popover closed; currentSentenceIndex adjusted for new surround offset
   * @sideeffect Closes popover; calls setCurrentSegment with updated surround offset
   */
  watch(
    () => state.surroundLines,
    (): void => {
      state.popoverVisible = false
      setCurrentSegment(surroundBeforeSentences.value.length, currentSegmentIndex.value) // resets currentSentenceIndex, but does not touch current segment
    },
  )
  // }}}
})

// {{{ computed popoverTarget
/**
 * @invariant Returns the DOM target ID for the popover: uses FSM segment indices when recording,
 *   popover-specific indices when not recording
 * @depends-on isRecording, currentSentenceIndex, currentSegmentIndex, state.popoverSentenceIndex, state.popoverSegmentIndex
 */
const popoverTarget = computed<string>(() => {
  if (isRecording.value) {
    return practiceTextSegmentId(currentSentenceIndex.value, currentSegmentIndex.value)
  } else {
    return practiceTextSegmentId(state.popoverSentenceIndex ?? 0, state.popoverSegmentIndex ?? 0)
  }
})
// }}}
// {{{ computed sentencesWithSurround
/**
 * @invariant Returns [surroundBefore, focusSentence, surroundAfter] for the practice overlay AnnotatedText
 * @depends-on surroundBeforeSentences, sentence, surroundAfterSentences
 */
const sentencesWithSurround = computed<Array<SentenceType>>(() => {
  if (!sentence.value) {
    logger.error('unexpected null sentence.value')
    return []
  }
  return [surroundBeforeSentences.value, sentence.value, surroundAfterSentences.value].flat(Infinity).filter(Boolean) as SentenceType[]
})
// }}}
// {{{ computed surroundOptions
const surroundOptions = computed<Array<number>>(() => {
  return Array.from(Array(state.sentences.length).keys())
})
// }}}

// {{{ computed visibility helpers
const hasTextSelection = computed<boolean>(() => !!state.aliyah || !!state.haftarah || !!state.sentenceGroup)

/** @invariant True when recordings or text-mode make the rendered text visible */
const textIsVisible = computed<boolean>(() => inTextMode(state.voiceStyleSet) || !!Object.keys(recordingsInStyle.value).length)

/** @invariant True when either the overlay or all-text view is showing text on screen */
const showToolbar = computed<boolean>(
  () => (!!sentence.value && hasTextSelection.value) || (!tagWarning.value && !!state.sentences.length && textIsVisible.value),
)
// }}}

// {{{ practice description
const practiceDescription = computed<string>(() => formatPracticeDescription(state.aliyah, state.haftarah, state.sentenceGroup))
// }}}

// {{{ overlay action objects
const overlayNavActions = {
  advanceToSentence,
  closePopover,
  closePracticeOverlay,
  handleSegmentBoundaryClick,
  handleSegmentClick,
  hasRecordingBySentenceIndex,
}

const overlaySessionActions = {
  setSilencePatience: session.setSilencePatience,
  setVoiceSensitivity: session.setVoiceSensitivity,
  toggleRecord: session.toggleRecord,
}

const overlayAudioActions = {
  loaded,
  pause,
  paused,
  play,
  playing,
  setLoadPlay,
  unloaded,
}

const overlayFSMActions = {
  handleJumpBack: fsm.handleJumpBack,
}
// }}}
</script>

<template>
  <!-- {{{ template -->
  <v-container fluid>
    <recording-upload-dialog
      :blob-url="state.pendingRecordingBlobUrl"
      :description="practiceDescription"
      :uploading="state.uploading"
      :visible="state.uploadRecordingOverlay"
      @discard="session.discardRecording"
      @save="session.saveRecording"
      @share="session.shareRecording"
    />
    <v-progress-circular v-if="store.getters.student.loading" indeterminate />

    <v-row v-if="isTeacher()">
      <v-col md="4">
        <student-select
          ref="barMitzvahSelect"
          v-model:barMitzvah="pendingStudentBarMitzvah"
          :bar-mitzvah-clearable="true"
          :external-set="true"
        />
      </v-col>
      <v-spacer />
      <v-col v-if="store.getters.student.barMitzvah" class="d-flex align-center justify-end" cols="auto">
        <v-btn color="primary" prepend-icon="mdi-open-in-new" size="small" variant="flat" @click="copyUrlToClipboard">
          Share Homework
        </v-btn>
      </v-col>
    </v-row>

    <div v-if="store.getters.student.barMitzvah">
      <sampling-overlay
        v-if="state.backgroundSampling"
        :on-click="
          () => {
            baseLevel = 0
            session.toggleRecord()
          }
        "
        :percent="state.backgroundSamplingPercent"
        :visible="state.backgroundSampling"
      />

      <!-- text/recordings selection -->
      <div style="position: relative">
        <v-btn
          v-if="!isTeacher()"
          color="grey-darken-1"
          prepend-icon="mdi-open-in-new"
          size="small"
          style="position: absolute; top: 0; right: 0; z-index: 1"
          variant="text"
          @click="copyUrlToClipboard"
        >
          Share Link
        </v-btn>
        <v-row>
          <v-col md="4" sm="5">
            <!-- Text -->
            <legacy-practice-select
              v-model:aliyah="state.aliyah"
              v-model:bar-mitzvah="store.getters.student.barMitzvah"
              v-model:haftarah="state.haftarah"
              v-model:sentence-group="state.sentenceGroup"
              v-model:sentences="state.sentences"
            />
          </v-col>
          <template v-if="!store.getters.student.loading && store.getters.audio.loading">
            <v-col class="mt-6" md="3">
              <v-progress-circular indeterminate />
            </v-col>
          </template>
          <template v-else-if="hasTextSelection">
            <!-- Practice: Reading/Singing/Text -->
            <v-col md="4">
              <voice-select
                v-model:voice="state.voiceStyleSet.voice"
                :annotated-sentences="state.annotatedSentences as Array<AnnotatedSentence>"
                :recordings="recordingsInStyle"
                :voice-style-set="state.voiceStyleSet"
              />
            </v-col>

            <!-- style selection (hidden in text mode — no recordings to filter) -->
            <legacy-practice-tag-selection
              v-if="!isInTextMode"
              :choosable-hidden-tags="choosableHiddenTags"
              :choosable-non-hidden-tags="choosableNonHiddenTags"
              :hidden-tags-stats="hiddenTagsStats"
              :state="state"
              :tag-warning-box="tagWarningBox"
            />
          </template>
        </v-row>
      </div>

      <!-- visual text manipulations -->
      <legacy-practice-text-toolbar
        v-if="showToolbar"
        class="mt-4"
        :is-in-sing-mode="isInSingMode"
        :max-font-size="MAX_FONT_SIZE"
        :min-font-size="MIN_FONT_SIZE"
        :refetch-audio="refetchAudio"
        :state="state"
        :ui-max-pitch-shift="UI_MAX_PITCH_SHIFT"
        :ui-min-pitch-shift="UI_MIN_PITCH_SHIFT"
      />

      <template v-if="DEBUG && state.fsmHistory.length">
        <v-row class="my-4">
          <v-col>
            baseLevel: {{ baseLevel && baseLevel.toFixed(2) }} <br />
            soundLevel: {{ state.soundLevel.toFixed(2) }}<br />
            state.backgroundSampling: {{ state.backgroundSampling }}<br />
            isRecording: {{ isRecording }}<br />
          </v-col>
        </v-row>
        <v-row>
          <v-col md="12"> FSM: {{ state.fsmHistory }} <br /> </v-col>
        </v-row>
      </template>

      <legacy-practice-overlay
        v-if="!!sentence && hasTextSelection"
        :audio="overlayAudioActions"
        :bar-mitzvah="store.getters.student.barMitzvah"
        :clickable-segment="clickableSegment"
        :clickable-sentence="clickableSentence"
        :current-segment-index="currentSegmentIndex"
        :fsm="overlayFSMActions"
        :is-recording="isRecording"
        :line-height="lineHeight"
        :nav="overlayNavActions"
        :next-key="NEXT_KEY"
        :popover-target="popoverTarget"
        :recordings-in-style="recordingsInStyle"
        :segment-boundary-style="segmentBoundaryStyle"
        :segment-style="segmentStyle"
        :segmentations="segmentations"
        :sentence="sentence"
        :sentence-style="sentenceStyle"
        :sentences-have-same-responsiveness="sentencesHaveSameResponsiveness"
        :sentences-with-surround="sentencesWithSurround"
        :session="overlaySessionActions"
        :state="state"
        :surround-before-start-index="surroundBeforeStartIndex"
        :surround-options="surroundOptions"
      />

      <!-- all text; must use v-show (not v-if) because this component populates annotatedSentences as a side effect -->
      <legacy-practice-all-text
        v-else-if="!tagWarning && !!state.sentences.length"
        v-show="textIsVisible"
        :bar-mitzvah="store.getters.student.barMitzvah"
        :handle-sentence-click="handleSentenceClick"
        :has-recording-by-annotated-sentence="hasRecordingByAnnotatedSentence"
        :line-height="lineHeight"
        :sentence-style="sentenceStyle"
        :sentences-have-same-responsiveness="sentencesHaveSameResponsiveness"
        :state="state"
      />
    </div>
  </v-container>
  <!-- }}} -->
</template>

<style>
/* {{{ style
 */
/* card should not change the line-height of annotated text */
.v-card__text {
  line-height: unset;
}

.torah {
  font-family: StamAshkenazCLM, sans-serif;
}

.bi:hover {
  cursor: pointer;
}

.closable:hover {
  cursor: pointer;
}

a:hover {
  text-decoration: none;
}

a.disabled {
  pointer-events: none;
  color: grey;
}

.transformation-v-select .v-select__selections {
  direction: rtl;
}

.transformation-v-select-option {
  direction: rtl;
  width: 100%;
  justify-content: flex-end;
}
/* }}} */
</style>
